// Settings / Usage (SUsage), admins only: time in the app (active minutes, counted while the app is open and in use)
// by person and working day, when the team is in it, and what the paid services cost this month. People never see
// their own numbers or anyone else's.
// 6.3 (Usage.dc.html): the four team numbers on top, the Team table (active per day, days active, peak hours, logged,
// preps on time, voice and recordings; a row picks the person for the heat map), Usage and delivery by week with its
// caveat, and how time is counted. Every number comes from the data (services/usage.ts says how each is worked out).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SessionUser, UsagePreps, UsageRange, UsageReport } from '@dpw/shared';
import { api } from '../api';
import { ago } from '../lib/format';
import { usePhone } from '../lib/media';
import { ErrorNote, Loading } from '../ui/bits';
import { Title, TopBar } from '../phone/kit';

const RANGES: [UsageRange, string][] = [['week', 'This week'], ['4weeks', 'Last 4 weeks'], ['quarter', 'Quarter']];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu'];
const HOURS = Array.from({ length: 16 }, (_, i) => String(7 + i).padStart(2, '0'));
const usd = (n: number) => `$${n.toFixed(2)}`;

/** 160 minutes as "2h 40m". */
const hm = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`);
const pct = (p: UsagePreps) => (p.of ? Math.round((100 * p.onTime) / p.of) : null);
const PERIOD: Record<UsageRange, string> = { week: 'this week', '4weeks': 'last 4 weeks', quarter: 'this quarter' };
function PrepChip({ p }: { p: UsagePreps }) {
  const v = pct(p);
  if (v === null) return <span className="xs mut">None due</span>;
  return <span className={`pl ${v >= 80 ? 'grn' : v >= 60 ? 'amb' : 'red'}`} title={`${p.onTime} of ${p.of} ready before they were due`}>{v}%</span>;
}

/** Usage and delivery: each week, team active hours beside the share of preps ready on time. */
function Delivery({ d }: { d: UsageReport }) {
  const top = Math.max(1, ...d.weeks.map(w => w.hours));
  return (
    <section className="panel" aria-label="Usage and delivery">
      <div className="sh"><b>Usage and delivery</b><span>Team, by week</span></div>
      <div className="udbars" role="list">
        {d.weeks.map(w => {
          const v = pct(w.preps);
          return (
            <div className="udw" role="listitem" key={w.from} aria-label={`Week of ${w.label}: ${w.hours} active hours, ${v === null ? 'no preps due' : `${w.preps.onTime} of ${w.preps.of} preps ready on time`}`}>
              <div className="udp">
                <i className="h" style={{ height: `${Math.max(2, (100 * w.hours) / top)}%` }} title={`${w.hours} active hours`} />
                <i className={`p${v === null ? ' none' : ''}`} style={{ height: `${v === null ? 2 : Math.max(2, v)}%` }} title={v === null ? 'No preps due' : `${v}% of preps ready on time`} />
              </div>
              <span className="xs mut mono">{w.label}</span>
            </div>
          );
        })}
      </div>
      <div className="rowc xs mut" style={{ gap: 14, marginTop: 8 }}><span className="rowc gap6"><i className="udkey h" />Active hours</span><span className="rowc gap6"><i className="udkey p" />Preps ready on time (share)</span></div>
      <p className="xs mut" style={{ margin: '8px 0 0', lineHeight: 1.5 }}>{d.trend}</p>
    </section>
  );
}

const RESEARCH_LABEL = { quick: 'Quick', standard: 'Standard', deep: 'Deep', follow: 'Followed links', manual: 'Added by hand' } as const;

export function UsagePage({ me }: { me: SessionUser }) {
  const phone = usePhone();
  const [range, setRange] = useState<UsageRange>('week');
  const [who, setWho] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['usage', range, who], queryFn: () => api.usage({ range, ...(who ? { userId: who } : {}) }), enabled: me.isAdmin });
  const all = useQuery({ queryKey: ['usage', range, null], queryFn: () => api.usage({ range }), enabled: me.isAdmin && !!who });
  if (!me.isAdmin) return <div className="empty">Usage is for admins.</div>;
  const everyone = (who ? all.data : q.data)?.people ?? [];
  const d = q.data;
  const max = Math.max(1, ...(d?.heat.flat() ?? [1]));
  const person = who ? everyone.find(p => p.id === who) : null;
  const body = !d ? (q.error ? <ErrorNote error={q.error} /> : <Loading />) : (
    <div className="col usg" style={{ gap: 14 }}>
      <div className="rowc" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="seg2" role="group" aria-label="Range">{RANGES.map(([k, l]) => <button key={k} type="button" className={range === k ? 'on' : ''} aria-pressed={range === k} onClick={() => setRange(k)}>{l}</button>)}</div>
      </div>
      <div className="xs mut">Time in the app only: active minutes, counted when the app is open and in use. People never see their own numbers or anyone else's.</div>
      <div className="rowc" role="group" aria-label="Person" style={{ flexWrap: 'wrap', gap: 6 }}>
        <button type="button" className={`pick${!who ? ' on' : ''}`} aria-pressed={!who} onClick={() => setWho(null)}>Everyone</button>
        {everyone.map(p => <button key={p.id} type="button" className={`pick${who === p.id ? ' on' : ''}`} aria-pressed={who === p.id} onClick={() => setWho(p.id)}>{p.name.startsWith('Dr.') ? p.name.split(' ').slice(0, 2).join(' ') : p.name.split(' ')[0]}</button>)}
      </div>
      <div className="g4 usgstats" role="group" aria-label="The team">
        <div className="panel" data-stat="active-today"><span className="k">Active today</span><b className="big">{d.stats.activeToday}</b><span className="xs mut">of {d.stats.people} people</span></div>
        <div className="panel" data-stat="per-day"><span className="k">Active time per day</span><b className="big">{hm(d.stats.avgPerDayMin)}</b><span className="xs mut">team average, {d.stats.workDays} working {d.stats.workDays === 1 ? 'day' : 'days'}</span></div>
        <div className="panel" data-stat="logged"><span className="k">Activities logged</span><b className="big">{d.stats.logged}</b><span className="xs mut">new and updated, {PERIOD[range]}</span></div>
        <div className="panel" data-stat="preps"><span className="k">Preps ready on time</span><b className="big">{pct(d.stats.preps) === null ? 'None due' : `${pct(d.stats.preps)}%`}</b><span className="xs mut">{d.stats.preps.of ? `${d.stats.preps.onTime} of ${d.stats.preps.of}, ${PERIOD[range]}` : PERIOD[range]}</span></div>
      </div>
      <section className="panel" aria-label="Team" style={{ overflowX: 'auto' }}>
        <div className="sh"><b>Team</b><span>Select a person to see their heat map</span></div>
        <table className="ut">
          <thead><tr><th>Person</th><th>Active per day</th><th>Days active</th><th>Peak hours</th><th>Activities logged</th><th>Preps on time</th><th>Voice and recordings</th></tr></thead>
          <tbody>{d.team.map(p => (
            <tr key={p.id} data-person={p.name} className={who === p.id ? 'on' : ''}>
              <td><button type="button" className="tperson" aria-pressed={who === p.id} onClick={() => setWho(who === p.id ? null : p.id)}><span className="who2">{p.initials}</span> {p.name}</button></td>
              <td className="mono xs">{hm(p.perDayMin)}</td>
              <td className="mono xs">{p.daysActive}/{p.daysOf}</td>
              <td className="mono xs mut">{p.peak ?? 'None yet'}</td>
              <td className="mono xs">{p.logged}</td>
              <td><PrepChip p={p.preps} /></td>
              <td className="mono xs mut">{p.voice.asks} {p.voice.asks === 1 ? 'request' : 'requests'} · {p.voice.recordedMin} min recorded</td>
            </tr>
          ))}</tbody>
        </table>
      </section>
      <div className="usplit">
      <section className="panel" aria-label="When in the app">
        <div className="sh"><b>When {person ? `${person.name.split(' ')[0]} is` : 'the team is'} in the app</b><span>Darker means more active minutes in that hour</span></div>
        <div className="heat" role="table" aria-label="Active minutes by day and hour">
          <div className="hrow" role="row"><span role="columnheader" />{HOURS.map(h => <span key={h} role="columnheader" className="hh">{h}</span>)}</div>
          {d.heat.map((row, i) => (
            <div className="hrow" role="row" key={DAYS[i]}><span role="rowheader" className="hd">{DAYS[i]}</span>
              {row.map((m, j) => <span key={j} role="cell" className="hc" title={`${DAYS[i]} ${HOURS[j]}:00, ${m} min`} style={{ opacity: m ? 0.15 + 0.85 * (m / max) : 0.06 }} />)}</div>
          ))}
        </div>
        <div className="xs mut" style={{ marginTop: 8 }}>Active minutes per hour, Sunday to Thursday, Riyadh time.</div>
      </section>
      <div className="col" style={{ gap: 14, minWidth: 0 }}>
        <Delivery d={d} />
        <section className="panel" aria-label="How time is counted">
          <div className="sh"><b>How time is counted</b></div>
          <div className="xs mut" style={{ lineHeight: 1.6 }}>Only while the app is open, visible and in use: the browser counts a minute when someone touched it (a click, a key, a scroll) in the last minute. A minute without input stops the clock. Signed in is not the same as using it.</div>
        </section>
      </div>
      </div>
      <section className="panel" aria-label="Hours per day, by person" style={{ overflowX: 'auto' }}>
        <div className="sh"><b>Hours per day, by person</b><span>Sun to Thu</span></div>
        <table className="ut">
          <thead><tr><th>Person</th><th>Role</th>{DAYS.map(x => <th key={x}>{x}</th>)}<th>Total</th><th>Shape</th><th>Last seen</th></tr></thead>
          <tbody>{d.people.map(p => {
            const top = Math.max(0.1, ...p.hours);
            return (
              <tr key={p.id} data-person={p.name}>
                <td><span className="who2">{p.initials}</span> {p.name}</td><td className="xs mut">{p.role}</td>
                {p.hours.map((h, i) => <td key={i} className="mono xs">{h.toFixed(1)}</td>)}
                <td className="mono xs"><b>{p.total.toFixed(1)}</b></td>
                <td><span className="shape" aria-hidden="true">{p.hours.map((h, i) => <i key={i} style={{ transform: `scaleY(${Math.max(0.04, h / top)})` }} />)}</span></td>
                <td className="xs mut">{p.lastSeen ? ago(p.lastSeen) : 'Never'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </section>
      <section className="panel" aria-label="Service costs">
        <div className="sh"><b>Service costs this month</b><span>{d.costs.month} · estimated · admins only</span></div>
        <div className="lrow"><span className="sm" style={{ flex: 1 }}>Assistant (Anthropic API)</span><b className="mono">{usd(d.costs.anthropic)}</b></div>
        <div className="lrow"><span className="sm" style={{ flex: 1 }}>Voice and transcription (ElevenLabs)</span><b className="mono">{usd(d.costs.elevenlabs)}</b></div>
        <div className="lrow"><span className="sm" style={{ flex: 1 }}>Hosting, storage and email (AWS)</span><b className="mono">{usd(d.costs.aws)}</b></div>
        <div className="xs mut" style={{ marginTop: 6 }}>From what each call used, at list prices (USAGE_RATES); hosting is the monthly amount set in USAGE_AWS_MONTHLY{d.costs.awsHosting ? ` (${usd(d.costs.awsHosting)})` : ', not set'}.</div>
      </section>
      <section className="panel" aria-label="Research spend per level">
        <div className="sh"><b>Research spend per level</b><span>{d.costs.month} · part of the Anthropic cost above</span></div>
        {d.research.length ? d.research.map(r => (
          <div className="lrow" key={r.level} data-level={r.level}>
            <span className="sm" style={{ flex: 1 }}>{RESEARCH_LABEL[r.level]}</span>
            <span className="xs mut mono">{r.level === 'follow' || r.level === 'manual' ? '' : `${r.tasks} ${r.tasks === 1 ? 'task' : 'tasks'} · `}{r.searches} {r.searches === 1 ? 'search' : 'searches'}</span>
            <b className="mono" style={{ minWidth: 70, textAlign: 'right' }}>{usd(r.cost)}</b>
          </div>
        )) : <div className="sm mut">No research this month.</div>}
        <div className="xs mut" style={{ marginTop: 6 }}>Tokens plus web searches at their list price; the scheduled research runs count as Standard.</div>
      </section>
    </div>
  );
  if (phone) return <div className="ph-page"><TopBar back={{ to: '/more', label: 'More' }} /><Title title="Usage" sub="Admins only" />{body}</div>;
  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="hdr"><div className="htl"><div className="crumb xs mut"><Link to="/settings">Settings</Link> / Usage</div><h1>Usage</h1><span className="pl">Admins only</span></div></div>
      {body}
    </div>
  );
}
